<template>
  <div class="customer-detail">
    <v-skeleton-loader
      class="custom-skeleton"
      v-if="pageLoading"
      type="article"
    >
    </v-skeleton-loader>

    <v-row v-else class="py-2">
      <!-- Column 1 (Left Grid) -->
      <v-col cols="12" md="6" class="py-0">
        <div class="d-flex flex-column">
          <!-- Status -->
          <div
            class="d-flex align-items-center justify-content-between py-2 border-bottom"
          >
            <div class="d-flex align-items-center">
              <v-icon small color="indigo darken-4" class="mr-2"
                >mdi-flag-outline</v-icon
              >
              <span class="text-secondary font-weight-500 font-size-14"
                >Status</span
              >
            </div>
            <div>
              <template v-if="lead.status">
                <v-chip
                  small
                  color="warning"
                  text-color="white"
                  label
                  class="font-weight-bold"
                >
                  <span>{{ getFormattedText(lead.status) }}</span>
                </v-chip>
              </template>
              <template v-else>
                <em class="text-muted font-weight-normal font-size-14"
                  >No Status</em
                >
              </template>
            </div>
          </div>

          <!-- Customer Type -->
          <div
            class="d-flex align-items-center justify-content-between py-2 border-bottom"
          >
            <div class="d-flex align-items-center">
              <v-icon small color="indigo darken-4" class="mr-2"
                >mdi-account-outline</v-icon
              >
              <span class="text-secondary font-weight-500 font-size-14"
                >Customer Type</span
              >
            </div>
            <div>
              <v-chip
                small
                outlined
                color="blue"
                label
                class="font-weight-500"
                v-if="lead.company_type == 'company'"
              >
                <span>Company</span>
              </v-chip>
              <v-chip
                small
                outlined
                color="deep-orange"
                label
                class="font-weight-500"
                v-if="lead.company_type == 'personal'"
              >
                <span>Individual</span>
              </v-chip>
            </div>
          </div>

          <!-- Company Name -->
          <div
            class="d-flex align-items-center justify-content-between py-2 border-bottom"
          >
            <div class="d-flex align-items-center">
              <v-icon small color="indigo darken-4" class="mr-2"
                >mdi-office-building-outline</v-icon
              >
              <span class="text-secondary font-weight-500 font-size-14"
                >Company Name</span
              >
            </div>
            <span class="font-size-14 text-dark text-capitalize">
              <template v-if="lead.company_name">{{
                lead.company_name
              }}</template>
              <template v-else
                ><em class="text-muted font-weight-normal"
                  >No company name</em
                ></template
              >
            </span>
          </div>

          <!-- Lead Date -->
          <div
            class="d-flex align-items-center justify-content-between py-2 border-bottom"
          >
            <div class="d-flex align-items-center">
              <v-icon small color="indigo darken-4" class="mr-2"
                >mdi-calendar-outline</v-icon
              >
              <span class="text-secondary font-weight-500 font-size-14"
                >Lead Date</span
              >
            </div>
            <span class="font-size-14 text-dark">
              <template v-if="lead.lead_date">{{
                formatDate(lead.lead_date)
              }}</template>
              <template v-else
                ><em class="text-muted font-weight-normal"
                  >No Lead Date</em
                ></template
              >
            </span>
          </div>

          <!-- Assign To -->
          <div class="d-flex align-items-center justify-content-between py-2">
            <div class="d-flex align-items-center">
              <v-icon small color="indigo darken-4" class="mr-2"
                >mdi-account-outline</v-icon
              >
              <span class="text-secondary font-weight-500 font-size-14"
                >Assign To</span
              >
            </div>
            <span class="font-size-14 text-dark text-capitalize">
              <template
                v-if="lead.assigned_user && lead.assigned_user.display_name"
              >
                {{ lead.assigned_user.display_name }}
              </template>
              <template v-else>
                <em class="text-muted font-weight-normal">No assigned user</em>
              </template>
            </span>
          </div>
        </div>
      </v-col>

      <!-- Column 2 (Right Grid) -->
      <v-col cols="12" md="6" class="py-0 pl-md-6 border-left-md">
        <div class="d-flex flex-column">
          <!-- Opportunity -->
          <div
            class="d-flex align-items-center justify-content-between py-2 border-bottom"
          >
            <div class="d-flex align-items-center">
              <v-icon small color="indigo darken-4" class="mr-2"
                >mdi-target</v-icon
              >
              <span class="text-secondary font-weight-500 font-size-14"
                >Opportunity</span
              >
            </div>
            <div>
              <template v-if="lead.opportunity">
                <v-chip
                  small
                  outlined
                  color="blue darken-2"
                  label
                  class="font-weight-500"
                >
                  <span>{{ lead.opportunity }}</span>
                </v-chip>
              </template>
              <template v-else>
                <em class="text-muted font-weight-normal font-size-14"
                  >No Opportunity</em
                >
              </template>
            </div>
          </div>

          <!-- Product Type -->
          <div v-if="false"
            class="d-flex align-items-center justify-content-between py-2 border-bottom"
          >
            <div class="d-flex align-items-center">
              <v-icon small color="indigo darken-4" class="mr-2"
                >mdi-package-variant</v-icon
              >
              <span class="text-secondary font-weight-500 font-size-14"
                >Product Type</span
              >
            </div>
            <div>
              <template v-if="lead.product_type">
                <v-chip
                  small
                  outlined
                  color="cyan darken-2"
                  label
                  class="font-weight-500"
                >
                  <span>{{ lead.product_type }}</span>
                </v-chip>
              </template>
              <template v-else>
                <em class="text-muted font-weight-normal font-size-14"
                  >no product type</em
                >
              </template>
            </div>
          </div>

          <!-- Source -->
          <div class="d-flex align-items-center justify-content-between py-2">
            <div class="d-flex align-items-center">
              <v-icon small color="indigo darken-4" class="mr-2"
                >mdi-earth</v-icon
              >
              <span class="text-secondary font-weight-500 font-size-14"
                >Source</span
              >
            </div>
            <div>
              <template v-if="lead.source">
                <v-chip
                  small
                  outlined
                  color="indigo"
                  label
                  class="font-weight-500"
                >
                  <span>{{ lead.source }}</span>
                </v-chip>
              </template>
              <template v-else>
                <em class="text-muted font-weight-normal font-size-14"
                  >No Source</em
                >
              </template>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import CommonMixin from "@/core/plugins/common-mixin";
import FileManagerMixin from "@/core/lib/file-manager/file.manager.mixin";

export default {
  name: "contact-details",
  title: "Lead",
  mixins: [CommonMixin, FileManagerMixin],
  data() {
    return {
      imageLoading: false,
    };
  },
  props: {
    lead: {
      type: Object,
      required: true,
      default: function () {
        return {};
      },
    },
    pageLoading: {
      type: Boolean,
      default: true,
    },
  },
  methods: {
    selectImage() {
      this.$refs["uploadImage"].$refs["input"].click();
    },

    statusColor(status) {
      const statusMapping = {
        Draft: "cyan",
        "Not Contacted": "grey",
        "Follow Up": "blue",
        Meeting: "orange",
        "Converted to Customer": "cyan",
        Accepted: "green",
        Rejected: "red",
      };

      return statusMapping[status] || "grey";
    },

    getFormattedText(status) {
      if (!status) return "";
      return status
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
  },
  components: {},
  computed: {
    leadActivated() {
      const _this = this;
      if (_this.lodash.isEmpty(_this.lead) === false) {
        return _this.lead.activated;
      }
      return false;
    },

    getProfileImage() {
      if (!this.lead.profile_logo) {
        return null;
      }
      return this.lead.profile_logo.file.url;
    },
    getBarcode() {
      if (!this.lead.barcode) {
        return "";
      }
      return this.lead.barcode;
    },
  },
};
</script>
